import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

/**
 * toppers ReviewsPage ``hero_content`` assembly: collapses the whole-field
 * StreamField into a SINGLE ``{% include_react %}`` island rendered inside
 * ``.customer-review__header-copy``. The surrounding ``.customer-review__header``
 * chrome + the ``WriteReviewButtonTemplate`` island are rendered separately.
 *
 * PER-BLOCK WRAPPER: wagtail ``StreamBlock`` render wraps EACH child in
 * ``<div class="w-block-{type} block-{type}">``; we reproduce that wrapper per
 * child so the DOM matches a native StreamField render.
 *
 * SERIALIZATION SHAPE -- the two block types serialize DIFFERENTLY:
 * - ``title`` (CharBlock) does NOT extend ``ClientRenderingBarrier``, so its
 *   serialized value is the RAW string. We emit it as an escaped React text node
 *   inside the load-bearing ``<h2 class="customer-review__header-title">``.
 * - ``subtitle`` (thelabui ``RichTextBlock`` -> ``ClientRenderingBarrier``) returns
 *   the rendered rich text, i.e. ``<div class="rich-text">...</div>``. We raw-inject
 *   that into the load-bearing ``.customer-review__header-subtitle`` wrapper, which
 *   is built here rather than in a server template.
 *
 * NO GLOBAL JS: static content (no carousel / no pre-hydration DOM injection).
 */
export const ReviewsPageHeroContent = (
    props: Immutable<{
        page: Pick<
            templates.ReviewsPageDocumentTemplate["page"],
            "hero_content"
        >;
    }>,
): React.JSX.Element | null => {
    const heroContent = props.page.hero_content;
    if (!heroContent) {
        return null;
    }
    return (
        <>
            {heroContent.value.map((block) => {
                const wrapperClassName = `w-block-${block.type} block-${block.type}`;
                if (block.type === "title") {
                    // CharBlock -> raw string; build the <h2> (escaped text).
                    return (
                        <div key={block.id} className={wrapperClassName}>
                            <h2 className="customer-review__header-title">
                                {block.value}
                            </h2>
                        </div>
                    );
                }
                // subtitle (RichTextBlock): raw-inject the rich-text HTML into the
                // load-bearing .customer-review__header-subtitle wrapper.
                return (
                    <div key={block.id} className={wrapperClassName}>
                        <div
                            className="customer-review__header-subtitle"
                            dangerouslySetInnerHTML={{ __html: block.value }}
                        />
                    </div>
                );
            })}
        </>
    );
};
